/*eslint max-lines-per-function: ["error", 238]*/
import React, { useEffect, useState } from 'react';

import { useAuth } from '../../../common/hooks/useAuth';
import { useSzafir } from '../../../common/hooks/useSzafir';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import {
  GetJudgmentDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { SimP1StatusEnum } from '../../../common/spdCore/validation/enums';
import { mustBeSentToP1 } from '../Judgments/mustBeSentToP1';

import { SigningInProgressModal } from './SigningInProgressModal';
import {
  sendJudgmentToP1 as sendJudgmentToP1Shared,
  signP1Document,
} from './sendJudgmentToP1';
import { signJudgmentPdfWithSzafir } from './signJudgmentPdfWithSzafir';

interface MultipleJudgmentSigningProps {
  onSignedFinished: (
    errors: string[],
    allCount: number,
    errorCount: number,
  ) => Promise<void> | void | undefined;
  registerId: string;
  userUid: string;
}

export const MultipleJudgmentSigning: React.FC<
  MultipleJudgmentSigningProps
> = props => {
  const [signingInProgress, setSigningInProgress] = useState(false);
  const [sendSummary, setSendSummary] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [allCount, setAllCount] = useState<number>(0);
  const [errorCount, setErrorCount] = useState<number>(0);
  const [progress, setProgress] = useState<string>('');
  const auth = useAuth();
  const spd = useSpdCore();
  const szafir = useSzafir();

  useEffect(() => {
    if (sendSummary) {
      void props.onSignedFinished(errors, allCount, errorCount);
      setSendSummary(false);
      setProgress('');
      setErrors([]);
      setAllCount(0);
      setErrorCount(0);
    }
  }, [errors, errorCount, allCount, props, sendSummary]);

  const onSign = async () => {
    setSigningInProgress(true);
    let judgments: GetJudgmentDto[] = [];
    try {
      judgments = await spd.judgments.getAllForUserRegisterToSign(
        props.userUid,
        props.registerId,
      );
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      setErrors(prevArray => [
        ...prevArray,
        `Błąd pobierania orzeczenia dla registerId: ${props.registerId} i userUid: ${props.userUid}`,
        ...errors,
      ]);
      setSigningInProgress(false);
      setSendSummary(true);
      return;
    }

    setAllCount(judgments.length);
    for (let index = 0; index < judgments.length; index++) {
      const judgment = judgments[index];
      setProgress(`podpisywanie ${index + 1} z ${judgments.length}`);
      const cancellationId = await getPendingCancellationId(judgment.id);
      if (cancellationId) {
        await signCancellation(judgment, cancellationId);
      } else if (mustBeSentToP1(judgment.article, judgment.judgmentDate)) {
        await spd.pdfPrinter.saveWithoutPrintJudgment(judgment.id);
        const signed =
          (await signPdfForP1(judgment)) &&
          (!judgment.article?.engPdfTemplateType ||
            (await signPdfForP1(judgment, true)));
        if (signed) {
          await sendJudgmentToP1(judgment.id);
        }
      } else {
        await signSaveAndSendEmail(judgment);
        if (judgment.article?.engPdfTemplateType) {
          await signSaveAndSendEmail(judgment, true);
        }
      }
    }
    setSigningInProgress(false);
    setSendSummary(true);
  };

  const getPendingCancellationId = async (judgmentId: string) => {
    try {
      const doc = await spd.judgmentP1Documents.getLastForJudgment(judgmentId);
      return doc.documentType === 'Cancellation' &&
        doc.status === SimP1StatusEnum.ToSign
        ? doc.id
        : null;
    } catch {
      return null;
    }
  };

  const signCancellation = async (
    judgment: GetJudgmentDto,
    documentId: string,
  ) => {
    try {
      await signP1Document(auth, szafir, spd, documentId);
    } catch (response) {
      setErrorCount(prevCount => prevCount + 1);
      const errors = await responseErrors(response as ResponseError);
      setErrors(prevArray => [
        ...prevArray,
        `Błąd podpisywania anulowania w SIM: ${judgment.number} - `,
        ...errors,
      ]);
    }
  };

  const signSaveAndSendEmail = async (
    judgment: GetJudgmentDto,
    english = false,
  ) => {
    try {
      const taskList = await spd.szafir.getTaskListForJudgment(
        judgment.id,
        english,
      );

      const [result, documentId, documentNumber, isEnglish] = await new Promise<
        [Buffer | null, string, string, boolean]
      >((resolve, reject) => {
        void szafir.signDocument(
          taskList,
          auth.userProfile?.pin || '',
          judgment.id,
          judgment.number || '',
          english,
          (result, documentId, documentNumber, isEnglish) =>
            resolve([result, documentId, documentNumber, isEnglish]),
          (error, documentId, documentNumber) => {
            signedFailed(error, documentId, documentNumber);
            reject(new Error(error));
          },
        );
      });
      await saveSignedPdfAndSendEmail(
        result,
        documentId,
        documentNumber,
        isEnglish,
      );
    } catch (response) {
      setErrorCount(prevCount => prevCount + 1);
      const errors = await responseErrors(response as ResponseError);
      setErrors(prevArray => [
        ...prevArray,
        `Błąd podpisywania dokumentu: ${judgment.number} - `,
        ...errors,
      ]);
    }
  };

  const saveSignedPdfAndSendEmail = async (
    result: Buffer | null,
    documentId: string,
    documentNumber: string,
    english: boolean,
  ) => {
    await spd.savedPdfs
      .savePdfFileForJudgment({
        entityId: documentId,
        file: result,
        english: english,
      })
      .then(async () => {
        await spd.emails.sendJudgmentByEmail(documentId, english);
      })
      .catch(async response => {
        setErrorCount(prevCount => prevCount + 1);
        const errors = await responseErrors(response as ResponseError);
        setErrors(prevArray => [
          ...prevArray,
          `Błąd: ${documentNumber} - `,
          ...errors,
        ]);
      });
  };

  const signPdfForP1 = async (
    judgment: GetJudgmentDto,
    english = false,
  ): Promise<boolean> => {
    try {
      await signJudgmentPdfWithSzafir(
        szafir,
        spd,
        auth.userProfile?.pin || '',
        judgment.id,
        judgment.number || '',
        english,
      );
      return true;
    } catch (response) {
      setErrorCount(prevCount => prevCount + 1);
      const errors = await responseErrors(response as ResponseError);
      setErrors(prevArray => [
        ...prevArray,
        `Błąd podpisywania dokumentu: ${judgment.number} - `,
        ...errors,
      ]);
      return false;
    }
  };

  const sendJudgmentToP1 = async (judgmentId: string) => {
    try {
      await sendJudgmentToP1Shared(auth, szafir, spd, judgmentId);
    } catch (response) {
      setErrorCount(prevCount => prevCount + 1);
      const errors = await responseErrors(response as ResponseError);
      setErrors(prevArray => [
        ...prevArray,
        `Błąd wysyłania do SIM: ${judgmentId} - `,
        ...errors,
      ]);
    }
  };

  const signedFailed = (
    error: string,
    documentId: string,
    documentNumber: string,
  ) => {
    setErrorCount(prevCount => prevCount + 1);
    setErrors(prevArray => [
      ...prevArray,
      `Błąd podpisywania dokumentu: ${documentNumber} - `,
      error,
    ]);
  };

  if (signingInProgress) {
    return <SigningInProgressModal message={progress} />;
  }

  return (
    <>
      <ConfirmationButton
        className="mb-3"
        confirmation="Czy na pewno rozpocząć podpisywanie wszystkich dostępnych do podpisu orzeczeń? Dotyczy to także dokumentów anulujących orzeczenia (oznaczonych jako Anulowanie)."
        variant="outline-primary"
        onOK={() => onSign()}
      >
        Podpisz wszystkie swoje orzeczenia
      </ConfirmationButton>
    </>
  );
};
